import React, { useEffect, useState, ReactNode } from 'react';
import { IconContainer, MessageText, ModalBackdrop, ModalContainer, ModalContent, SuccessIcon } from './SuccessModal.styles';

// Types
export interface SuccessModalProps {
  visible: boolean;
  message?: string;
  defaultMessage: string;
  icon?: ReactNode;
  autoHide?: boolean;
  autoHideDuration?: number;
  onHide?: () => void;
}

// Main Component
export const PopUpAlert: React.FC<SuccessModalProps> = ({
  visible,
  message,
  defaultMessage,
  autoHide = true,
  autoHideDuration = 1500,
  onHide
}) => {
  // Using portal approach - add root element if not exists
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    setPortalRoot(root);

    // Cleanup
    return () => {
      if (root && root.childNodes.length === 0 && document.body.contains(root)) {
        document.body.removeChild(root);
      }
    };
  }, []);

  // Handle body scroll when modal is visible
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [visible]);

  // Auto-hide functionality
  useEffect(() => {
    if (visible && autoHide) {
      const timer = setTimeout(() => {
        if (onHide) {
          onHide();
        }
      }, autoHideDuration);

      return () => clearTimeout(timer);
    }
  }, [visible, autoHide, autoHideDuration, onHide]);

  if (!portalRoot) return null;

  // Using ReactDOM.createPortal equivalent approach with styled components
  return (
    <ModalBackdrop $visible={visible}>
      <ModalContainer $visible={visible}>
        <ModalContent>
          <IconContainer>
            <SuccessIcon />
          </IconContainer>
          <MessageText>
            {message || defaultMessage}
          </MessageText>
        </ModalContent>
      </ModalContainer>
    </ModalBackdrop>
  );
};

PopUpAlert.displayName = 'PopUpAlert';

export default PopUpAlert;
